// 入院経路モバイル page (/m/a-route)
// - 主要 KPI 4 件: 紹介入院率 / 予定入院率 / 新規入院数 / 病床稼働率 (当月)
// - 経由カテゴリ別 (HP/CL/腎CL/地域ケア) × 予定/緊急 grid (当月)
// - 24ヶ月推移 (新規入院数 sparkline)

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { SENTIMENT_BG, SENTIMENT_RING } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { fmtInt, fmtPct } from "../../lib/format";
import type { RouteResponse, RouteRow } from "@shared/api";

const KEIYU_ORDER = ["HP", "CL", "腎CL", "地域ケア"];
const YOTEI_ORDER = ["予定", "緊急"];

export function RouteMobile() {
  const { data: meta } = useMeta();
  const [months, setMonths] = useState<12 | 24>(24);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // asOfMonth state (= periodTo). ←→ で月送り可能。
  const [asOfMonth, setAsOfMonth] = useState<string | null>(null);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;
  const periodTo = asOfMonth ?? meta.dateMax;
  const periodFrom = monthShift(periodTo, -(months - 1));
  const asOfYm = periodTo.slice(0, 7);
  const canPrev = monthShift(periodTo, -1).slice(0, 7) >= meta.dateMin.slice(0, 7);
  const canNext = monthShift(periodTo, +1).slice(0, 7) <= meta.dateMax.slice(0, 7);

  return (
    <MobileShell
      title="🚪 入院経路 速報"
      prototypeId="A"
      prototypeName="入院経路"
      asOfMonth={periodTo}
      onPrevMonth={() => setAsOfMonth(monthShift(periodTo, -1))}
      onNextMonth={() => setAsOfMonth(monthShift(periodTo, +1))}
      canPrevMonth={canPrev}
      canNextMonth={canNext}
      headerRight={
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="text-slate-500 hover:text-slate-800 active:bg-slate-100 rounded p-1.5 text-base leading-none"
          title="期間切替"
          aria-label="フィルタ"
        >⚙</button>
      }
    >
      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
        <span className="bg-slate-100 px-1.5 py-0.5 rounded">直近 {months} ヶ月 / 病院全体</span>
      </div>

      <RouteContent
        periodFrom={periodFrom}
        periodTo={periodTo}
        asOfYm={asOfYm}
      />

      <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
        診療科別ランキングは PC 版へ。
      </p>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setDrawerOpen(false)}>
          <aside
            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-800">⚙ フィルタ</h2>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-400 text-xl">×</button>
            </header>
            <div className="px-4 py-3 space-y-4 text-sm flex-1">
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1.5">期間</div>
                <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded p-1">
                  {[12, 24].map((m) => (
                    <button
                      key={m}
                      onClick={() => setMonths(m as 12 | 24)}
                      className={`text-xs py-1.5 rounded ${months === m ? "bg-white font-semibold shadow-sm" : "text-slate-500"}`}
                    >直近 {m} ヶ月</button>
                  ))}
                </div>
              </div>
            </div>
            <footer className="px-4 py-3 border-t border-slate-200">
              <button onClick={() => setDrawerOpen(false)} className="w-full text-xs py-2 bg-slate-900 text-white rounded font-semibold">適用して閉じる</button>
            </footer>
          </aside>
        </div>
      )}
    </MobileShell>
  );
}

function RouteContent({ periodFrom, periodTo, asOfYm }: {
  periodFrom: string; periodTo: string; asOfYm: string;
}) {
  const { data, isLoading } = useQuery<RouteResponse>({
    queryKey: ["route-mobile", periodFrom, periodTo],
    queryFn: () => api.route({
      granularity: "小", iryo: "合計",
      periodFrom, periodTo,
      deptBreakdown: false,
    }),
    placeholderData: (prev) => prev,
  });

  // 当月 rows
  const currentRows: RouteRow[] = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => r.date.slice(0, 7) === asOfYm);
  }, [data, asOfYm]);

  // KPI 計算 (当月)
  const kpi = useMemo(() => {
    const totalNew = currentRows.reduce((s, r) => s + r.count, 0);
    const referred = currentRows.filter((r) => r.紹介 === 1).reduce((s, r) => s + r.count, 0);
    const planned  = currentRows.filter((r) => r.予定緊急 === "予定").reduce((s, r) => s + r.count, 0);
    return {
      newPatients: totalNew,
      referralRate: totalNew > 0 ? referred / totalNew : null,
      plannedRate:  totalNew > 0 ? planned / totalNew : null,
    };
  }, [currentRows]);

  // 経由 × 予定/緊急 grid (当月)
  const grid = useMemo(() => {
    const g: Record<string, Record<string, number>> = {};
    for (const k of KEIYU_ORDER) g[k] = { 予定: 0, 緊急: 0 };
    for (const r of currentRows) {
      if (g[r.経由]) g[r.経由][r.予定緊急] = (g[r.経由][r.予定緊急] ?? 0) + r.count;
    }
    return g;
  }, [currentRows]);

  // 月別 新規入院数 (sparkline)
  const newPatientTrend = useMemo<(number | null)[]>(() => {
    if (!data) return [];
    const m = new Map<string, number>();
    for (const r of data.rows) {
      const ym = r.date.slice(0, 7);
      m.set(ym, (m.get(ym) ?? 0) + r.count);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }, [data]);

  return (
    <>
      <section>
        <div className="text-xs text-slate-500 mb-1">
          {asOfYm} 主要指標
          {isLoading && <span className="ml-2 text-[9px] text-slate-400">loading…</span>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <KpiTile label="新規入院数" value={fmtInt(kpi.newPatients)} unit="人" sentiment="neutral" trend={newPatientTrend} />
          <KpiTile label="紹介入院率" value={kpi.referralRate != null ? fmtPct(kpi.referralRate, 1) : "—"} unit="" sentiment="neutral" />
          <KpiTile label="予定入院率" value={kpi.plannedRate != null ? fmtPct(kpi.plannedRate, 1) : "—"} unit="" sentiment="neutral" />
          <KpiTile label="緊急入院率" value={kpi.plannedRate != null ? fmtPct(1 - kpi.plannedRate, 1) : "—"} unit="" sentiment="neutral" />
        </div>
      </section>

      <section>
        <h2 className="text-xs font-semibold text-slate-700 mb-1">経由 × 予定/緊急 (当月 件数)</h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600">
            <div>経由</div>
            <div className="text-right">予定</div>
            <div className="text-right">緊急</div>
            <div className="text-right">計</div>
          </div>
          {KEIYU_ORDER.map((k) => {
            const sumK = YOTEI_ORDER.reduce((s, y) => s + (grid[k]?.[y] ?? 0), 0);
            return (
              <div key={k} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0">
                <div className="text-slate-800">{k}</div>
                <div className="text-right tabular-nums text-slate-600">{grid[k]?.["予定"] ?? 0}</div>
                <div className="text-right tabular-nums text-slate-600">{grid[k]?.["緊急"] ?? 0}</div>
                <div className="text-right tabular-nums font-semibold text-slate-800 w-12">{sumK}</div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

function KpiTile({ label, value, unit, sentiment, trend }: {
  label: string; value: string; unit: string;
  sentiment: "positive" | "neutral" | "warning" | "danger" | "unknown";
  trend?: (number | null)[];
}) {
  return (
    <div className={`p-2.5 rounded-lg ring-1 ${SENTIMENT_RING[sentiment]} ${SENTIMENT_BG[sentiment]}`}>
      <div className="text-[11px] text-slate-600 font-medium">{label}</div>
      {unit && <div className="text-[9px] text-slate-400">{unit}</div>}
      <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{value}</div>
      {trend && trend.length > 1 && (
        <div className="mt-0.5 -mx-1">
          <Sparkline values={trend} width={140} height={20} showLast />
        </div>
      )}
    </div>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
